import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';

const AcademicYears = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, yearId: null });
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    isActive: true
  });

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get('/api/academic-years');
      setAcademicYears(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des années universitaires:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (editingYear) {
        await axios.put(`/api/academic-years/${editingYear._id}`, formData, config);
        showSuccess('Année universitaire modifiée avec succès');
      } else {
        await axios.post('/api/academic-years', formData, config);
        showSuccess('Année universitaire ajoutée avec succès');
      }
      fetchAcademicYears();
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError('Erreur lors de la sauvegarde de l\'année universitaire');
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      await axios.delete(`/api/academic-years/${id}`, config);
      fetchAcademicYears();
      showSuccess('Année universitaire supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur lors de la suppression de l\'année universitaire');
    }
  };

  const confirmDelete = (yearId) => {
    setDeleteConfirm({ show: true, yearId });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.yearId) {
      await handleDelete(deleteConfirm.yearId);
    }
    setDeleteConfirm({ show: false, yearId: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, yearId: null });
  };

  const openModal = (year = null) => {
    setEditingYear(year);
    setFormData(year ? {
      name: year.name,
      startDate: formatDateForInput(year.startDate),
      endDate: formatDateForInput(year.endDate),
      isActive: year.isActive
    } : {
      name: '',
      startDate: '',
      endDate: '',
      isActive: true
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingYear(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">Années Universitaires</h1>
          <button 
            onClick={() => openModal()} 
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
          >
            + Ajouter
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {academicYears.map(year => (
            <div key={year._id} className={`bg-white rounded-lg shadow-sm border-2 p-4 hover:shadow-md transition-shadow ${
              year.isActive ? 'border-primary-200 bg-primary-50' : 'border-gray-200'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📅</span>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{year.name}</h3>
                    <div className="flex items-center text-sm mt-1">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        year.isActive ? 'bg-green-500' : 'bg-gray-400'
                      }`}></span>
                      <span className={year.isActive ? 'text-green-700' : 'text-gray-600'}>
                        {year.isActive ? 'Année en cours' : 'Année inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => openModal(year)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => confirmDelete(year._id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="w-5">📅</span>
                  <span><strong>Début:</strong> {formatDateForDisplay(year.startDate)}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="w-5">📅</span>
                  <span><strong>Fin:</strong> {formatDateForDisplay(year.endDate)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {academicYears.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune année universitaire</h3>
            <p className="text-gray-600">Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingYear ? 'Modifier Année Universitaire' : 'Ajouter Année Universitaire'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'année</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: 2023-2024"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Année universitaire en cours</span>
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingYear ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">🗑️ Confirmer la suppression</h3>
                <button onClick={cancelDelete} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">Êtes-vous sûr de vouloir supprimer cette année universitaire ? Cette action est irréversible.</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicYears;